import {CommonModule} from "@angular/common";
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
  ViewChild,
} from "@angular/core";
import {ProductionLineFormComponent} from "../production-line-form/production-line-form.component";
import {ProductionLineService} from "src/app/features/admin/data-access/production-line.service";
import {
  ActivatedRoute,
  NavigationStart,
  Router,
  RouterModule,
} from "@angular/router";
import {SelectionService} from "src/app/services/selection.service";
import {NavigationService} from "src/app/shared/data-access/navigation.service";
import {MatCardModule} from "@angular/material/card";
import {
  CdkDragDrop,
  CdkDropList,
  CdkDrag,
  moveItemInArray,
  CdkDragHandle,
} from "@angular/cdk/drag-drop";
import {MatExpansionModule} from "@angular/material/expansion";
import {BootstrapIcon} from "src/app/services/header.service";
import {WorkstationService} from "src/app/features/admin/data-access/workstation.service";
import {Workstation} from "src/app/shared/interfaces/workstation";
import {ApiService} from "src/app/services/api.service";
import {SaveCancelButtonsComponent} from "../../../components/save-cancel-buttons/save-cancel-buttons.component";
import {Subscription} from "rxjs";

@Component({
  selector: "app-production-line-details-page",
  templateUrl: "./production-line-details-page.component.html",
  styleUrls: ["./production-line-details-page.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ProductionLineFormComponent,
    MatCardModule,
    MatExpansionModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SaveCancelButtonsComponent,
  ],
})
export class ProductionLineDetailsPageComponent {
  @ViewChild(ProductionLineFormComponent)
  productionLineForm!: ProductionLineFormComponent;
  productionLineService = inject(ProductionLineService);
  workstationService = inject(WorkstationService);
  selectionService = inject(SelectionService);
  navigationService = inject(NavigationService);
  apiService = inject(ApiService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  BootstrapIcon = BootstrapIcon;
  localWorkstations = signal<Workstation[]>([]);
  localWorkstationsDeleted = signal<Workstation[]>([]);
  private routerSubscription!: Subscription;

  protected productionLineBeingEdited = computed(() => {
    const url = this.navigationService.currentUrl();
    const productionLineUuid = url.split("line/")[1]?.split("?")[0];
    if (productionLineUuid) {
      return (
        this.productionLineService
          .productionLines()
          .find((line) => line.id === productionLineUuid) || null
      );
    } else {
      return null;
    }
  });

  constructor() {
    effect(
      () => {
        const workstations = this.workstationService.filteredWorkstations();
        untracked(() => {
          this.localWorkstations.set(this.deepCopyWorkstations(workstations));
        });
      },
      {allowSignalWrites: true}
    );
  }

  ngOnInit() {
    // get active productionline after page refresh
    this.selectionService.changeProductionLine(
      this.navigationService.currentUrl().split("line/")[1].split("?")[0]
    );
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.localWorkstations.set([]);
      }
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    const updatedWorkstations = this.localWorkstations();
    moveItemInArray(
      updatedWorkstations,
      event.previousIndex,
      event.currentIndex
    );
    this.updateSequence(updatedWorkstations);
    this.localWorkstations.set(updatedWorkstations);
  }

  updateSequence(workstations: Workstation[]) {
    for (let [newPosition, workstation] of workstations.entries()) {
      /* // console.log(
        `Moving ${workstation.name} from position ${workstation.position} to ${newPosition} `
      ); */
      workstation.position = newPosition;
    }
  }

  private deepCopyWorkstations(workstations: Workstation[]): Workstation[] {
    return workstations.map((ws) => ({...ws}));
  }

  async deleteWorkstation(deletedWorkstation: Workstation) {
    if (
      confirm(
        "Soll der Arbeitsplatz und alle damit verbindenen Stillstandsmeldungen und Wissensdatenbankeinträge wirklich gelöscht werden?"
      ) === true
    ) {
      // update local state
      let updatedWorkstations = this.localWorkstations()
        .filter((ws) => ws.id !== deletedWorkstation.id)
        .map(
          (ws): Workstation =>
            ws.position > deletedWorkstation.position
              ? {...ws, position: ws.position - 1}
              : ws
        );
      this.localWorkstations.set(updatedWorkstations);
      this.localWorkstationsDeleted.update((ws) => [...ws, deletedWorkstation]);
    }
  }

  editWorkstation(workstation: Workstation) {
    this.workstationService.workstationBeingEdited$.next(workstation);
    this.router.navigate(["admin/workstations/workstation/", workstation.id], {
      queryParams: {formAction: "edit"},
    });
  }

  save() {
    // delete workstations in localWorkstationsDeleted
    this.localWorkstationsDeleted().forEach((ws) => {
      this.workstationService.delete$.next(ws.id!);
    });

    // update workstations in localWorkstations
    this.localWorkstations().forEach((ws) => {
      this.workstationService.update$.next(ws);
    });

    // save form data and navigate to overview
    this.productionLineForm.save();
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }
}
